import { phonex } from "@munesoft/phonex";

/**
 * Detects the country dial code (e.g., "+91") from a phone number input
 * using the AI-first phonex library.
 *
 * @param phoneNumber - Raw phone number string (with or without + prefix)
 * @returns The matching dial_code (e.g., "+91") or null if no match found
 */
export function detectCountryCode(phoneNumber: string): string | null {
  if (!phoneNumber || phoneNumber.trim().length < 2) return null;

  const raw = phoneNumber.trim();
  const cleaned = raw.replace(/[^\d+]/g, "");
  if (cleaned.length < 2) return null;

  // Case 1: Explicit '+' prefix (e.g., +4155552671)
  if (cleaned.startsWith("+")) {
    const result = phonex(cleaned);
    if (result && result.countryCallingCode) {
      return `+${result.countryCallingCode}`;
    }
    return null;
  }

  // Case 2: Raw number (e.g., 4155552671). Use 'suggest' to avoid false dial code snapping
  // e.g. 4155552671 should suggest US (+1), not Switzerland (+41)
  const suggested = phonex.suggest(cleaned);
  if (suggested && suggested.suggestions && suggested.suggestions.length > 0) {
    let topSuggestion = suggested?.suggestions[0]?.e164;

    // UnivDiam has a largely Indian user base. If a 10-digit number is entered 
    // and starts with 6, 7, 8, or 9, it's almost certainly an Indian mobile number.
    // However, if the user explicitly formats it like a US number (e.g. (627) 953-6247),
    // we should respect the US format and not override.
    const looksLikeUS = /^[\s\(\.\-]*\d{3}[\s\)\.\-]+\d{3}[\s\.\-]+\d{4}$/.test(raw) || /^\d{3}-\d{3}-\d{4}$/.test(raw) || /^\(\d{3}\)\s*\d{3}-\d{4}$/.test(raw);
    
    if (!looksLikeUS && cleaned.length === 10 && /^[6789]/.test(cleaned)) {
      const indiaSuggestion = suggested.suggestions.find((s: any) => s.country === 'IN');
      if (indiaSuggestion) {
        topSuggestion = indiaSuggestion.e164;
      }
    }

    const result = phonex(topSuggestion);
    if (result && result.countryCallingCode) {
      return `+${result.countryCallingCode}`;
    }
  }

  // Fallback: If suggest didn't find anything, try strict parsing, 
  // but require length to be at least 6 to avoid snapping on tiny inputs.
  const result = phonex(cleaned);
  if (result && result.countryCallingCode && cleaned.length >= 6) {
    return `+${result.countryCallingCode}`;
  }

  return null;
}
